/**
 * Selector de zonas para vincular a un trigger (v0.3).
 * Muestra jardines y sus zonas; solo se envían zoneIds al backend.
 */

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { MapPin, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface GardenOption {
  id: string;
  name: string;
  companyId: string;
  zones: { id: string; name: string; gardenId: string }[];
}

interface TriggerZoneSelectorProps {
  gardens: GardenOption[];
  selectedZoneIds: string[];
  onChange: (zoneIds: string[]) => void;
  disabled?: boolean;
  className?: string;
}

export function TriggerZoneSelector({
  gardens,
  selectedZoneIds,
  onChange,
  disabled = false,
  className,
}: TriggerZoneSelectorProps) {
  const toggleZone = (zoneId: string) => {
    if (disabled) return;
    const next = selectedZoneIds.includes(zoneId)
      ? selectedZoneIds.filter((id) => id !== zoneId)
      : [...selectedZoneIds, zoneId];
    onChange(next);
  };

  const toggleAllInGarden = (garden: GardenOption) => {
    if (disabled) return;
    const zoneIds = garden.zones.map((z) => z.id);
    const allSelected = zoneIds.every((id) => selectedZoneIds.includes(id));
    if (allSelected) {
      onChange(selectedZoneIds.filter((id) => !zoneIds.includes(id)));
    } else {
      const merged = [...new Set([...selectedZoneIds, ...zoneIds])];
      onChange(merged);
    }
  };

  const isGardenAllSelected = (garden: GardenOption) =>
    garden.zones.length > 0 &&
    garden.zones.every((z) => selectedZoneIds.includes(z.id));

  if (gardens.length === 0) {
    return (
      <div className={cn('rounded-lg border border-dashed p-4 text-sm text-muted-foreground', className)}>
        No hay jardines con zonas para esta empresa. Selecciona una empresa o crea jardines y zonas.
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      <p className="text-xs text-muted-foreground">
        Sin zonas seleccionadas: el trigger se aplica a <strong>todas</strong> las controladoras de la empresa.
        Con zonas: solo las controladoras de las zonas elegidas.
      </p>
      <ScrollArea className="h-[240px] rounded-lg border p-2">
        <Accordion type="multiple" className="w-full">
          {gardens.map((garden) => (
            <AccordionItem key={garden.id} value={garden.id}>
              <AccordionTrigger className="hover:no-underline py-2">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-sm">{garden.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({garden.zones.length} zonas)
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="pl-6 pr-2 space-y-1">
                  <button
                    type="button"
                    onClick={() => toggleAllInGarden(garden)}
                    disabled={disabled}
                    className="text-xs text-primary hover:underline"
                  >
                    {isGardenAllSelected(garden) ? 'Deseleccionar todas' : 'Seleccionar todas'}
                  </button>
                  {garden.zones.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-1">Sin zonas</p>
                  ) : (
                    garden.zones.map((zone) => (
                      <label
                        key={zone.id}
                        className={cn(
                          'flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-muted/50 cursor-pointer text-sm',
                          disabled && 'opacity-60 cursor-not-allowed'
                        )}
                      >
                        <Checkbox
                          checked={selectedZoneIds.includes(zone.id)}
                          onCheckedChange={() => toggleZone(zone.id)}
                          disabled={disabled}
                        />
                        <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{zone.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </ScrollArea>
      {selectedZoneIds.length > 0 && (
        <p className="text-xs text-muted-foreground">
          <strong>{selectedZoneIds.length}</strong> zona(s) seleccionada(s)
        </p>
      )}
    </div>
  );
}
